import React from 'react';
import { observer } from 'mobx-react';

import { PlayArrow, Block, ArrowForward } from '@mui/icons-material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { Tooltip } from '@mui/material';

import { ScheduleInstanceResponse, ScheduleStatuses } from 'utils/api/modules/schedules/schedules.types';
import { Button } from 'components/Button';

import { formatMoment } from 'utils/formatters/timezone';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';

import { FooterContainer, ReasonContainer } from './styles';

interface DrawerFooterProps {
  data: ScheduleInstanceResponse;
  auditLoading: boolean;
  onStartAudit: () => void;
  onContinueAudit: () => void;
  onViewAudit: () => void;
  onWontDoClick: () => void;
}

const WontDoReason = ({ reason }: { reason: string }): React.ReactElement => (
  <ReasonContainer>
    <InfoOutlineIcon htmlColor="#0772E5" />
    <div>
      <h1>Won&apos;t Do Reason</h1>
      <p>{reason}</p>
    </div>
  </ReasonContainer>
);

const WontDoButton = ({ onClick }: { onClick: () => void }): React.ReactElement => (
  <Button
    variant="outlined"
    sx={{ borderColor: '#BDBDBD', color: '#000' }}
    startIcon={<Block htmlColor="#BDBDBD" />}
    onClick={onClick}
  >
    Won&apos;t Do
  </Button>
);

const FooterContent = ({
  data,
  auditLoading,
  onStartAudit,
  onContinueAudit,
  onViewAudit,
  onWontDoClick,
}: DrawerFooterProps): React.ReactElement => {
  const { status } = data;

  if (status === ScheduleStatuses.WONT_DO) {
    return <WontDoReason reason={data.reason || ''} />;
  }

  if (status === ScheduleStatuses.COMPLETED) {
    return (
      <Button variant="outlined" startIcon={<ArrowForward />} sx={{ flex: 1 }} onClick={onViewAudit}>
        View audit
      </Button>
    );
  }

  if (status === ScheduleStatuses.IN_PROGRESS) {
    return (
      <>
        <Button
          variant="outlined"
          startIcon={<ArrowForward />}
          sx={{ flex: 1 }}
          onClick={onContinueAudit}
          disabled={auditLoading}
        >
          Continue audit
        </Button>
        <WontDoButton onClick={onWontDoClick} />
      </>
    );
  }

  const isStartDateInFuture = new Date(data.startDate) > new Date() || status === ScheduleStatuses.UPCOMING;
  const isTemplateArchived = data.templateIsActive === false;
  const isDisabled = auditLoading || isStartDateInFuture || isTemplateArchived;

  const tooltipTitle = isTemplateArchived
    ? 'Template has been archived'
    : isStartDateInFuture
      ? `This audit can be started from ${formatMoment(data.startDate, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}`
      : '';

  return (
    <>
      <Tooltip title={tooltipTitle} arrow>
        <span style={{ flex: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            sx={{ width: '100%' }}
            onClick={onStartAudit}
            disabled={isDisabled}
          >
            {auditLoading ? 'Starting...' : 'Start audit'}
          </Button>
        </span>
      </Tooltip>
      <WontDoButton onClick={onWontDoClick} />
    </>
  );
};

const ObservedFooterContent = observer(FooterContent);

const DrawerFooter: React.FC<DrawerFooterProps> = (props) => (
  <FooterContainer>
    <ObservedFooterContent {...props} />
  </FooterContainer>
);

export default DrawerFooter;
